import React from 'react'

import { Box, useTheme } from '@mui/material'
import { ChevronRight } from 'lucide-react'

import { ListRow } from 'components/UI/ListRow'

/**
 * VERA-637 — the "pick one, it happens now" row a sell sheet step is built out of.
 *
 * Extracted from `SaleModeSelect`, which had it as a private `ModeRow`, at the moment a SECOND
 * step needed the identical thing (§36 — one hand-roll is allowed, the second is a shared
 * component).
 *
 * ## VERA-632 review: this is a LIST ROW, not a filled card
 *
 * It used to draw itself on `fieldSurfaceSx` — a grey filled box per option, stacked with a gap.
 * Reviewed and rejected: grey filled containers are not this app's language for "choose one of
 * these", and a column of them reads as a stack of alerts rather than a list you pick from. The
 * app already has one answer for a list of choices, `ListRow` — leading glyph, label, sublabel,
 * a hairline between rows and a trailing chevron — and it is what the certificate page's own
 * Provenance / Scans / Activity rows use. Rebuilding on it means these sheets stop being an
 * outlier, and it is §28/§47 applied rather than restated: reuse the shared row instead of
 * styling a bespoke one.
 *
 * The CHEVRON is not decoration. Every one of these rows advances to a further step, and the
 * chevron is what says so before the tap — the same promise it makes on the group row and the
 * certificate page's push rows.
 *
 * ## Still an ACTION row, not a selectable card (§34)
 *
 * Tapping advances immediately; there is no persistent "selected" state to distinguish from
 * hover, so there is no tint-for-hover / border-for-selected pair to honour. What hover and
 * focus do here is darken the row, and the `:focus-visible` half is NOT optional — it is the
 * only affordance a keyboard user gets, and omitting it makes the row invisible to them while
 * looking identical to everyone else.
 */

export interface SheetChoiceRowProps {
  icon: React.ReactNode
  title: string
  /** One sentence saying what choosing this actually leads to — not a restatement of the title. */
  description: string
  onClick: () => void
  /**
   * Whether this row draws its bottom hairline. Each row is wrapped in its own tap target here,
   * so `ListRow`'s `:last-of-type` fallback would match EVERY row and the list would lose its
   * separators — the exact trap that prop documents. Callers pass
   * `divider={index !== rows.length - 1}`.
   */
  divider?: boolean
}

export const SheetChoiceRow = ({
  icon,
  title,
  description,
  onClick,
  divider,
}: SheetChoiceRowProps) => {
  const theme = useTheme()

  return (
    <Box
      onClick={onClick}
      role='button'
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      sx={{
        cursor: 'pointer',
        // Negative inset + matching padding so the hover fill reaches past the sheet's own
        // gutter, which is what makes a full-width row read as tappable rather than a tinted
        // rectangle floating inside it.
        mx: -2,
        px: 2,
        borderRadius: 1,
        transition: 'background-color .12s ease',
        '&:hover, &:focus-visible': {
          backgroundColor: theme.palette.grey[50],
          outline: 'none',
        },
        '&:focus-visible': {
          boxShadow: `inset 0 0 0 2px ${theme.palette.text.primary}`,
        },
      }}
    >
      <ListRow
        leading={icon}
        label={title}
        sublabel={description}
        divider={divider}
        meta={
          <Box sx={{ display: 'flex', color: theme.palette.text.secondary }}>
            <ChevronRight size={18} strokeWidth={1.5} />
          </Box>
        }
      />
    </Box>
  )
}
